import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, Trophy, Loader2, ListChecks, Medal, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAiJob, isStopped } from "@/lib/aiJob";
import { useAuth } from "@/context/AuthContext";
import { logWarn } from "@/lib/log";

export function StreakBadge({ streak }) {
  if (!streak) return null;
  const { days, active_today } = streak;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border ${days ? "border-orange-300/40 bg-orange-500/10 text-orange-200" : "border-white/10 text-neutral-500"}`} data-testid="group-streak">
      <Flame className={`h-3.5 w-3.5 ${days ? "text-orange-300" : ""}`} />
      {days ? `${days}-day group streak` : "No streak yet"}
      {!active_today && <span className="text-neutral-500">· play or share today{days ? " to keep it" : " to start one"}</span>}
    </span>
  );
}

const MEDAL = { 1: ["Top of the week", "bg-yellow-300 text-black"], 2: ["2nd this week", "bg-neutral-300 text-black"], 3: ["3rd this week", "bg-orange-400 text-black"] };

export function useWeeklyBoard(classId) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => http.get(`/groups/${classId}/leaderboard`).then((r) => setRows(r.data))
    .catch((e) => logWarn("Leaderboard load failed", e)), [classId]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const badges = useMemo(() => {
    const b = {};
    (rows || []).filter((r) => r.wins + r.correct > 0).slice(0, 3).forEach((r, i) => { b[r.user_id] = i + 1; });
    return b;
  }, [rows]);
  return { rows, badges };
}

export function RankBadge({ rank, testid }) {
  if (!rank) return null;
  const [title, cls] = MEDAL[rank];
  return (
    <span title={title} className={`inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${cls}`} data-testid={testid}>
      <Medal className="h-2.5 w-2.5" />#{rank}
    </span>
  );
}

export function GroupLeaderboard({ rows, badges }) {
  const { user } = useAuth();
  return (
    <div className="rounded-2xl border border-white/10 bg-[#121725] p-5" data-testid="group-leaderboard">
      <p className="text-sm font-medium mb-1 flex items-center gap-2"><Trophy className="h-4 w-4 text-yellow-300" /> This week</p>
      <p className="text-[11px] text-neutral-500 mb-3">Ranked by game wins, then correct answers. Top three earn a badge.</p>
      {!rows?.length && <p className="text-xs text-neutral-500">No games played this week yet.</p>}
      <ol className="space-y-1.5">
        {rows?.map((r, i) => (
          <li key={r.user_id} className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded-lg ${r.user_id === user.user_id ? "bg-sky-500/10" : ""}`} data-testid={`leaderboard-row-${r.user_id}`}>
            <span className="w-4 text-neutral-500 text-xs">{i + 1}</span>
            <span className="flex-1 truncate flex items-center gap-1.5">{r.name}<RankBadge rank={badges[r.user_id]} /></span>
            <span className="text-xs text-yellow-200">{r.wins}W</span>
            <span className="text-xs text-neutral-400 w-12 text-right">{r.correct} ✓</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function HelpfulButton({ classId, note, onChanged }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const own = note.author_id === user.user_id;
  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await http.post(`/groups/${classId}/notes/${note.note_id}/helpful`);
      onChanged(note.note_id, data);
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <button onClick={toggle} disabled={busy || own} title={own ? "Your note" : note.i_found_helpful ? "Undo helpful" : "Mark as helpful"} data-testid={`note-helpful-${note.note_id}`}
      className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5 transition-colors disabled:cursor-default ${note.i_found_helpful ? "border-sky-300/50 bg-sky-500/15 text-sky-200" : "border-white/10 text-neutral-400 hover:bg-white/5"}`}>
      <ThumbsUp className="h-3.5 w-3.5" /> Helpful{note.helpful_count > 0 && <span data-testid={`note-helpful-count-${note.note_id}`}>· {note.helpful_count}</span>}
    </button>
  );
}

export function NoteQuizButton({ classId, note }) {
  const navigate = useNavigate();
  const { refreshEnt, ent } = useAuth();
  const [busy, setBusy] = useState(false);
  const job = useAiJob({ onStopped: refreshEnt });
  const canHost = !!ent?.features.groups;
  const go = async () => {
    if (busy) { job.stop(); setBusy(false); return; }
    setBusy(true);
    try {
      let id = note.quiz_game_id;
      if (!id) {
        const { data } = await job.run((o) => http.post(`/groups/${classId}/notes/${note.note_id}/quiz`, null, o));
        id = data.game_id;
        refreshEnt();
      }
      await http.post(`/games/${id}/join`);
      navigate(`/app/games/${id}`);
    } catch (e) { if (!isStopped(e) && e.response?.status !== 402) toast.error(formatError(e)); } finally { setBusy(false); }
  };
  if (!note.quiz_game_id && !canHost) return null;
  return (
    <button onClick={go} data-testid={`note-quiz-${note.note_id}`}
      className="text-xs px-3 py-1.5 rounded-full border border-emerald-300/30 text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-1.5 disabled:opacity-50">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ListChecks className="h-3.5 w-3.5" />}
      {busy ? "Stop" : note.quiz_game_id ? "Take quiz" : "Quiz the group · 1 message"}
    </button>
  );
}
